import React, { useState, useEffect } from "react";
import {
  UserIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  ClockIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import ConfirmationModal from "../components/ConfirmationModal";
import { bookingAPI } from "../services/api";

const Profile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "overview");
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [clientServices, setClientServices] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "warning",
  });
  const [cancellationModal, setCancellationModal] = useState({
    isOpen: false,
    booking: null,
    reason: "",
    within12Hours: false,
  });

  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();

  // Handle tab change and update URL
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Initialize form data when user data is available
  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        email: user.email || "",
        phone: user.phone || "",
      });
    }
  }, [user]);

  // Function to fetch bookings
  const fetchBookings = async (page = 1) => {
    try {
      const bookingsData = await bookingAPI.getMyBookings(page);
      setBookings(bookingsData.results || bookingsData || []);

      // Update pagination info
      if (bookingsData.count !== undefined) {
        // Calculate total pages dynamically from API response
        let totalPages = 1;

        if (bookingsData.count > 0) {
          if (bookingsData.next || bookingsData.previous) {
            // Use current page results length as page size if available
            const currentPageSize = bookingsData.results ? bookingsData.results.length : 1;
            if (bookingsData.next) {
              // Not the last page, use current results length as page size
              totalPages = Math.ceil(bookingsData.count / currentPageSize);
            } else {
              // Last page, we are on the final page
              totalPages = page;
            }
          } else {
            // Only one page
            totalPages = 1;
          }
        }

        setBookingsPagination({
          currentPage: page,
          totalPages: totalPages,
          hasNext: !!bookingsData.next,
          hasPrevious: !!bookingsData.previous,
          count: bookingsData.count
        });
      }
    } catch (error) {
      console.error("Error loading bookings:", error);
      toast.error("Erreur lors du chargement des réservations");
    }
  };

  // Function to fetch client services
  const fetchClientServices = async () => {
    try {
      const servicesData = await bookingAPI.getMyClientServices();
      setClientServices(servicesData || []);
    } catch (error) {
      console.error("Error loading services:", error);
      toast.error("Erreur lors du chargement des services");
    }
  };

  // Load user bookings and services
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;

      setLoadingData(true);
      try {
        // Load bookings and client services in parallel
        const bookingsPage = parseInt(searchParams.get('bookings_page')) || 1;
        await Promise.all([fetchBookings(bookingsPage), fetchClientServices()]);
      } catch (error) {
        console.error("Error loading user data:", error);
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoadingData(false);
      }
    };

    loadUserData();
  }, [user]);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notificationsPagination, setNotificationsPagination] = useState({
    currentPage: parseInt(searchParams.get('notifications_page')) || 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
    count: 0
  });
  const [bookingsPagination, setBookingsPagination] = useState({
    currentPage: parseInt(searchParams.get('bookings_page')) || 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
    count: 0
  });

  const fetchNotifications = async (page = 1) => {
    try {
      setLoading(true);
      const data = await bookingAPI.getNotifications(page);
      console.log("Notifications API response:", data);
      setNotifications(data.results || data);

      // Update pagination info
      if (data.count !== undefined) {
        // Calculate total pages dynamically from API response
        let totalPages = 1;

        if (data.count > 0) {
          if (data.next || data.previous) {
            // Use current page results length as page size if available
            const currentPageSize = data.results ? data.results.length : 1;
            if (data.next) {
              // Not the last page, use current results length as page size
              totalPages = Math.ceil(data.count / currentPageSize);
            } else {
              // Last page, we are on the final page
              totalPages = page;
            }
          } else {
            // Only one page
            totalPages = 1;
          }
        }

        setNotificationsPagination({
          currentPage: page,
          totalPages: totalPages,
          hasNext: !!data.next,
          hasPrevious: !!data.previous,
          count: data.count
        });
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to update URL parameters
  const updateUrlParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 1) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  };

  useEffect(() => {
    const notificationsPage = parseInt(searchParams.get('notifications_page')) || 1;
    fetchNotifications(notificationsPage);
  }, [searchParams]);

  // Handle mark all notifications as read
  const handleMarkAllNotificationsRead = async () => {
    try {
      await bookingAPI.markAllNotificationsRead();
      toast.success("Toutes les notifications ont été marquées comme lues");
      // Refresh notifications
      await fetchNotifications(notificationsPagination.currentPage);
    } catch (error) {
      toast.error("Erreur lors du marquage des notifications");
      console.error("Error marking all notifications as read:", error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await updateProfile(formData);
      toast.success("Profil mis à jour avec succès !");
      setIsEditing(false);
    } catch (error) {
      toast.error(error.message || "Erreur lors de la mise à jour");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Les nouveaux mots de passe ne correspondent pas");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error(
        "Le nouveau mot de passe doit contenir au moins 6 caractères"
      );
      return;
    }

    setIsLoading(true);

    try {
      // Call API to update password
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/change-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la mise à jour du mot de passe');
      }

      toast.success("Mot de passe mis à jour avec succès !");
      setShowPasswordForm(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      toast.error(error.message || "Erreur lors du changement de mot de passe");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Déconnexion réussie");
    navigate("/");
  };

  // Calculate if cancellation is within 12 hours
  const isWithin12Hours = (bookingDate, bookingTime) => {
    const bookingDateTime = new Date(`${bookingDate}T${bookingTime}:00`);
    const now = new Date();
    const timeDifference = bookingDateTime.getTime() - now.getTime();
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference <= 12;
  };

  // Handle booking cancellation
  const handleCancelBooking = (booking) => {
    const within12Hours = isWithin12Hours(booking.date, booking.time);

    setCancellationModal({
      isOpen: true,
      booking: booking,
      reason: "",
      within12Hours: within12Hours,
    });
  };

  const handleConfirmCancellation = async () => {
    const { booking, reason, within12Hours } = cancellationModal;

    setCancellationModal({ ...cancellationModal, isOpen: false });
    setCancellingBooking(booking.id);

    try {
      // Call the real API to cancel booking with reason
      const response = await bookingAPI.cancelBooking(booking.id, reason);

      if (response.booking_deleted) {
        toast.success("Réservation supprimée sans pénalité");
      } else if (response.penalty_applied) {
        toast.success("Réservation annulée avec pénalité (1 séance décomptée)");
      } else {
        toast.success("Réservation annulée sans pénalité");
      }

      // Refresh all profile data
      const bookingsPage = parseInt(searchParams.get('bookings_page')) || 1;
      const notificationsPage = parseInt(searchParams.get('notifications_page')) || 1;
      await Promise.all([
        fetchBookings(bookingsPage),
        fetchClientServices(),
        fetchNotifications(notificationsPage)
      ]);
    } catch (error) {
      toast.error(
        error.message || "Erreur lors de l'annulation de la réservation"
      );
      console.error("Cancel booking error:", error);
    } finally {
      setCancellingBooking(null);
    }
  };

  // Compute active services from real data
  const activeServices = clientServices
    .filter((service) => service.status === "active")
    .map((service) => ({
      id: service.id,
      type: service.service_type?.name || "Service",
      totalSessions:
        service.service_type?.type === "pack"
          ? service.total_sessions
          : service.service_type?.session_count,
      usedSessions:
        service.service_type?.type === "pack"
          ? service.used_sessions
          : service.current_month_used,
      remainingSessions:
        service.service_type?.type === "pack"
          ? service.total_sessions - service.used_sessions
          : service.service_type?.session_count - service.current_month_used,
      startDate: service.start_date,
      endDate: service.end_date,
      monthlySessions:
        service.service_type?.type === "abonnement"
          ? service.service_type?.session_count
          : null,
      usedThisMonth:
        service.service_type?.type === "abonnement"
          ? service.current_month_used
          : null,
      currentMonth:
        service.service_type?.type === "abonnement"
          ? new Date().toLocaleDateString("fr-FR", {
              month: "long",
              year: "numeric",
            })
          : null,
      status: service.status,
    }));

  // Compute upcoming bookings from real data
  const upcomingBookings = bookings
    .filter((booking) => {
      const bookingDate = new Date(
        `${booking.session?.date}T${booking.session?.start_time}`
      );
      const now = new Date();
      return bookingDate > now && booking.status === "confirmed";
    })
    .map((booking) => ({
      id: booking.id,
      date: booking.session?.date,
      time: booking.session?.start_time?.slice(0, 5),
      coach: booking.session?.coach?.user?.full_name,
      studio: booking.session?.location?.name,
      status: booking.status,
    }))
    .sort(
      (a, b) =>
        new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`)
    );

  // Compute booking history from real data
  const bookingHistory = bookings
    .filter((booking) => {
      const bookingDate = new Date(
        `${booking.session?.date}T${booking.session?.start_time}`
      );
      const now = new Date();
      return bookingDate <= now || booking.status === "cancelled";
    })
    .map((booking) => ({
      id: booking.id,
      date: booking.session?.date,
      time: booking.session?.start_time?.slice(0, 5),
      coach: booking.session?.coach?.user?.full_name,
      studio: booking.session?.location?.name,
      status: booking.status,
    }))
    .sort(
      (a, b) =>
        new Date(`${b.date}T${b.time}`) - new Date(`${a.date}T${a.time}`)
    );

  const tabs = [
    { id: "overview", name: "Vue d'ensemble", icon: UserIcon },
    { id: "bookings", name: "Mes réservations", icon: CalendarDaysIcon },
    { id: "services", name: "Mes services", icon: CreditCardIcon },
    {
      id: "notifications",
      name: "Mes notifications",
      icon: BellIcon,
    },
    {
      id: "settings",
      name: "Paramètres",
      icon: ({ className }) => (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </svg>
      ),
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "text-blue-600 bg-blue-100";
      case "completed":
        return "text-green-600 bg-green-100";
      case "cancelled":
        return "text-red-600 bg-red-100";
      case "active":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  // Pagination component with ellipsis
  const Pagination = ({ pagination, onPageChange }) => {
    if (pagination.totalPages <= 1) return null;

    const { currentPage, totalPages } = pagination;

    // Generate page numbers with ellipsis logic
    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;

      if (totalPages <= maxVisiblePages) {
        // Show all pages if total is small
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);

        if (currentPage > 3) {
          pages.push('...');
        }

        // Show pages around current page
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        for (let i = start; i <= end; i++) {
          if (i !== 1 && i !== totalPages) {
            pages.push(i);
          }
        }

        if (currentPage < totalPages - 2) {
          pages.push('...');
        }

        // Always show last page
        if (totalPages > 1) {
          pages.push(totalPages);
        }
      }

      return pages;
    };

    const pageNumbers = getPageNumbers();
    

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => onPageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPrevious}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>
          <button
            onClick={() => onPageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNext}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Page <span className="font-medium">{pagination.currentPage}</span> sur{' '}
              <span className="font-medium">{pagination.totalPages}</span>
              {pagination.count && (
                <span className="ml-2 text-gray-500">
                  ({pagination.count} élément{pagination.count > 1 ? 's' : ''} au total)
                </span>
              )}
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevious}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Précédent</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>
              {pageNumbers.map((page, index) => (
                page === '...' ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      page === pagination.currentPage
                        ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                    }`}
                  >
                    {page}
                  </button>
                )
              ))}
              <button
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Suivant</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  const getStatusText = (status) => {
    switch (status) {
      case "confirmed":
        return "Confirmé";
      case "completed":
        return "Terminé";
      case "cancelled":
        return "Annulé";
      case "active":
        return "Actif";
      default:
        return status;
    }
  };

  // Calculate real statistics
  const getNextSession = () => {
    const now = new Date();
    const upcomingBookings = bookings
      .filter((booking) => {
        const sessionDate = new Date(
          `${booking.session?.date}T${booking.session?.start_time}`
        );
        return sessionDate > now && booking.status === "confirmed";
      })
      .sort((a, b) => {
        const dateA = new Date(`${a.session?.date}T${a.session?.start_time}`);
        const dateB = new Date(`${b.session?.date}T${b.session?.start_time}`);
        return dateA - dateB;
      });

    return upcomingBookings[0] || null;
  };

  const getThisMonthCompletedSessions = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return bookings.filter((booking) => {
      const sessionDate = new Date(booking.session?.date);
      return (
        sessionDate.getMonth() === currentMonth &&
        sessionDate.getFullYear() === currentYear &&
        booking.status === "completed"
      );
    }).length;
  };

  const getActiveService = () => {
    return (
      clientServices.find((service) => service.status === "active") || null
    );
  };

  const formatSessionDateTime = (session) => {
    if (!session?.date || !session?.start_time) return "Non défini";

    const date = new Date(session.date);
    const time = session.start_time.slice(0, 5); // Get HH:MM format

    return `${date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    })}, ${time}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-2xl">
                {user?.full_name?.charAt(0) || "U"}
              </span>
            </div>
            <div className="ml-6">
              <h1 className="text-2xl font-bold text-gray-900">
                {user?.full_name || "Utilisateur"}
              </h1>
              <p className="text-gray-600">{user.email}</p>
              <p className="text-sm text-gray-500">
                Membre depuis{" "}
                {new Date(user.joinDate).toLocaleDateString("fr-FR")}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            {/* Desktop Navigation */}
            <nav className="-mb-px hidden sm:flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors duration-200 ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>

            {/* Mobile Navigation */}
            <nav className="-mb-px flex sm:hidden overflow-x-auto px-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-3 px-3 border-b-2 font-medium text-xs flex flex-col items-center min-w-0 flex-shrink-0 transition-colors duration-200 ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <tab.icon className="h-6 w-6 mb-1" />
                  <span className="truncate max-w-[60px]">{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-8 w-8 text-blue-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-blue-600">
                          Prochaine séance
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {getNextSession()
                            ? formatSessionDateTime(getNextSession().session)
                            : "Aucune séance prévue"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-6">
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-8 w-8 text-green-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-green-600">
                          Séances ce mois
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {getThisMonthCompletedSessions()} terminée
                          {getThisMonthCompletedSessions() > 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-6">
                    <div className="flex items-center">
                      <CreditCardIcon className="h-8 w-8 text-purple-600" />
                      <div className="ml-4">
                        <p className="text-sm font-medium text-purple-600">
                          Services actifs
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {activeServices.length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Prochaines réservations
                  </h3>
                  <div className="space-y-3">
                    {upcomingBookings.slice(0, 3).map((booking) => (
                      <div
                        key={booking.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">
                              {formatSessionDateTime(booking.session)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {booking.session?.coach?.user?.full_name ||
                                "Coach"}{" "}
                              - {booking.session?.location?.name || "Studio"}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            booking.status
                          )}`}
                        >
                          {getStatusText(booking.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Bookings Tab */}
            {activeTab === "bookings" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Réservations à venir
                  </h3>
                  <div className="space-y-4">
                    {upcomingBookings.map((booking) => (
                      <div
                        key={booking.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <CalendarDaysIcon className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                              <p className="text-lg font-medium text-gray-900">
                                {new Date(booking.date).toLocaleDateString(
                                  "fr-FR",
                                  {
                                    weekday: "long",
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  }
                                )}
                              </p>
                              <div className="flex items-center mt-1 space-x-4">
                                <div className="flex items-center text-sm text-gray-500">
                                  <ClockIcon className="h-4 w-4 mr-1" />
                                  {booking.time}
                                </div>
                                <div className="flex items-center text-sm text-gray-500">
                                  <UserIcon className="h-4 w-4 mr-1" />
                                  {booking.coach}
                                </div>
                                <div className="flex items-center text-sm text-gray-500">
                                  <MapPinIcon className="h-4 w-4 mr-1" />
                                  {booking.studio}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span
                              className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(
                                booking.status
                              )}`}
                            >
                              {getStatusText(booking.status)}
                            </span>
                            {booking.status === "confirmed" && (
                              <div className="flex flex-col items-end space-y-1">
                                <button
                                  onClick={() => handleCancelBooking(booking)}
                                  disabled={cancellingBooking === booking.id}
                                  className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                  {cancellingBooking === booking.id ? (
                                    <>
                                      <svg
                                        className="animate-spin -ml-1 mr-1 h-3 w-3 text-red-600"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                        ></circle>
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                      </svg>
                                      Annulation...
                                    </>
                                  ) : (
                                    "Annuler"
                                  )}
                                </button>
                                {isWithin12Hours(
                                  booking.date,
                                  booking.time
                                ) && (
                                  <span className="text-xs text-orange-600 font-medium">
                                    ⚠️ Pénalité appliquée
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Historique
                  </h3>
                  <div className="space-y-3">
                    {bookingHistory.map((booking) => (
                      <div
                        key={booking.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            {booking.status === "completed" ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">
                              {new Date(booking.date).toLocaleDateString(
                                "fr-FR"
                              )}{" "}
                              à {booking.time}
                            </p>
                            <p className="text-sm text-gray-500">
                              {booking.coach} - {booking.studio}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            booking.status
                          )}`}
                        >
                          {getStatusText(booking.status)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Pagination for bookings */}
                  <Pagination
                    pagination={bookingsPagination}
                    onPageChange={(page) => {
                      updateUrlParams({ bookings_page: page });
                      fetchBookings(page);
                    }}
                  />
                </div>
              </div>
            )}

            {/* Services Tab */}
            {activeTab === "services" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Services actifs
                  </h3>
                  <div className="space-y-4">
                    {activeServices.map((service) => (
                      <div
                        key={service.id}
                        className="border border-gray-200 rounded-lg p-6"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-medium text-gray-900">
                            {service.type}
                          </h4>
                          <span
                            className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(
                              service.status
                            )}`}
                          >
                            {getStatusText(service.status)}
                          </span>
                        </div>

                        {service.totalSessions ? (
                          <div>
                            <div className="flex justify-between text-sm text-gray-600 mb-2">
                              <span>Progression</span>
                              <span>
                                {service.usedSessions}/{service.totalSessions}{" "}
                                séances
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{
                                  width: `${
                                    (service.usedSessions /
                                      service.totalSessions) *
                                    100
                                  }%`,
                                }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-sm text-gray-500 mt-2">
                              <span>
                                Début:{" "}
                                {new Date(service.startDate).toLocaleDateString(
                                  "fr-FR"
                                )}
                              </span>
                              <span>
                                Fin:{" "}
                                {new Date(service.endDate).toLocaleDateString(
                                  "fr-FR"
                                )}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex justify-between text-sm text-gray-600 mb-2">
                              <span>{service.currentMonth}</span>
                              <span>
                                {service.usedThisMonth}/
                                {service.monthlySessions} séances
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{
                                  width: `${
                                    (service.usedThisMonth /
                                      service.monthlySessions) *
                                    100
                                  }%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === "notifications" && (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Notifications
                    </h3>
                    {notifications.some(n => !n.is_read) && (
                      <button
                        onClick={handleMarkAllNotificationsRead}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg md:text-sm text-xs transition-colors"
                      >
                        Tout marquer comme lu
                      </button>
                    )}
                  </div>
                  <div className="space-y-4">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className="border border-gray-200 rounded-lg p-6"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="md:text-lg text-sm font-medium text-gray-900">
                            {notification.title}
                          </h4>
                          <span
                            className={`px-3 py-1 md:text-sm text-xs font-medium rounded-full ${getStatusColor(
                              notification.is_read ? "read" : "unread"
                            )}`}
                          >
                            {notification.is_read ? "Lu" : "Non lu"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {notification.message}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Pagination for notifications */}
                  <Pagination
                    pagination={notificationsPagination}
                    onPageChange={(page) => {
                      updateUrlParams({ notifications_page: page });
                      fetchNotifications(page);
                    }}
                  />
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === "settings" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">
                    Paramètres du compte
                  </h3>

                  {/* Profile Information Section */}
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4">
                      Informations personnelles
                    </h4>

                    {!isEditing ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nom complet
                            </label>
                            <p className="text-sm text-gray-900">{user?.full_name}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email
                            </label>
                            <p className="text-sm text-gray-900">{user?.email}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Téléphone
                            </label>
                            <p className="text-sm text-gray-900">{user?.phone || "Non renseigné"}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setIsEditing(true)}
                          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          Modifier les informations
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleProfileUpdate} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nom complet
                            </label>
                            <input
                              type="text"
                              name="full_name"
                              value={formData.full_name}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Téléphone
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
                          >
                            {isLoading ? "Enregistrement..." : "Enregistrer"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsEditing(false)}
                            className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg transition-colors"
                          >
                            Annuler
                          </button>
                        </div>
                      </form>
                    )}
                  </div>

                  {/* Password Change Section */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4">
                      Changer le mot de passe
                    </h4>

                    {!showPasswordForm ? (
                      <button
                        onClick={() => setShowPasswordForm(true)}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        Modifier le mot de passe
                      </button>
                    ) : (
                      <form onSubmit={handlePasswordUpdate} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mot de passe actuel
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword ? "text" : "password"}
                              name="currentPassword"
                              value={passwordData.currentPassword}
                              onChange={handlePasswordChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                            >
                              {showPassword ? "🙈" : "👁️"}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nouveau mot de passe
                          </label>
                          <input
                            type={showPassword ? "text" : "password"}
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                            minLength="8"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Confirmer le nouveau mot de passe
                          </label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirmPassword"
                              value={passwordData.confirmPassword}
                              onChange={handlePasswordChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                            >
                              {showConfirmPassword ? "🙈" : "👁️"}
                            </button>
                          </div>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
                          >
                            {isLoading ? "Mise à jour..." : "Mettre à jour le mot de passe"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowPasswordForm(false);
                              setPasswordData({
                                currentPassword: "",
                                newPassword: "",
                                confirmPassword: "",
                              });
                            }}
                            className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg transition-colors"
                          >
                            Annuler
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancellation Modal */}
      {cancellationModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Annuler la réservation
              </h3>

              {cancellationModal.booking && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Date:</strong>{" "}
                    {new Date(
                      cancellationModal.booking.date
                    ).toLocaleDateString("fr-FR", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Heure:</strong> {cancellationModal.booking.time}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Coach:</strong> {cancellationModal.booking.coach}
                  </p>
                </div>
              )}

              {cancellationModal.within12Hours ? (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    ⚠️ <strong>ATTENTION:</strong> Cette annulation est
                    effectuée moins de 12h avant la séance. Une séance sera
                    décomptée de votre service (pénalité).
                  </p>
                </div>
              ) : (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    ✅ Cette annulation est effectuée plus de 12h avant la
                    séance. Aucune pénalité ne sera appliquée.
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison de l'annulation (optionnel)
                </label>
                <textarea
                  value={cancellationModal.reason}
                  onChange={(e) =>
                    setCancellationModal({
                      ...cancellationModal,
                      reason: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  rows="3"
                  placeholder="Expliquez pourquoi vous annulez cette réservation..."
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() =>
                    setCancellationModal({
                      ...cancellationModal,
                      isOpen: false,
                    })
                  }
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmCancellation}
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors ${
                    cancellationModal.within12Hours
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-yellow-600 hover:bg-yellow-700"
                  }`}
                >
                  Confirmer l'annulation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() =>
          setConfirmationModal({ ...confirmationModal, isOpen: false })
        }
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        loading={cancellingBooking !== null}
      />
    </div>
  );
};

export default Profile;
